import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AppProvider } from "@/context/AppContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { I18nProvider } from "@/i18n";

import PublicLayout from "@/components/public/PublicLayout";
import ScrollManager from "@/components/public/ScrollManager";
import HomePage from "@/pages/HomePage";
import ToolPage from "@/pages/ToolPage";
import ContentPage from "@/pages/ContentPage";
import FaqPage from "@/pages/FaqPage";
import NotFoundPage from "@/pages/NotFoundPage";

import ProtectedRoute from "@/components/admin/ProtectedRoute";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import { lazy, Suspense, useEffect, useState } from "react";

const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminPlatforms = lazy(() => import("@/pages/admin/AdminPlatforms"));
const AdminTools = lazy(() => import("@/pages/admin/AdminTools"));
const AdminSEOContent = lazy(() => import("@/pages/admin/AdminSEOContent"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminAds = lazy(() => import("@/pages/admin/AdminAds"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminLogs = lazy(() => import("@/pages/admin/AdminLogs"));
const LearnLibraryPage = lazy(() => import("@/pages/LearnLibraryPage"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const AdminArticles = lazy(() => import("@/pages/admin/AdminArticles"));
const AdminArticleEditor = lazy(() => import("@/pages/admin/AdminArticleEditor"));
const AdminArticleCategories = lazy(() => import("@/pages/admin/AdminArticleCategories"));
const AdminHomeContent = lazy(() => import("@/pages/admin/AdminHomeContent"));

function App({
  RouterComponent = BrowserRouter,
  routerProps,
  initialData,
  initialPageData = {},
  trackVisits = true,
}) {
  const [hydrationPageData, setHydrationPageData] = useState(initialPageData);

  useEffect(() => {
    // SSR payload is one-use hydration input; client-side route visits fetch fresh public data.
    setHydrationPageData({});
  }, []);

  return (
    <div className="App">
      <I18nProvider lang="ar">
        <AppProvider initialData={initialData} trackVisits={trackVisits}>
          <RouterComponent {...routerProps}>
            <ScrollManager />
            <AdminAuthProvider>
              <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite" dir="rtl">
                  جارٍ تحميل الصفحة…
                </div>
              }>
                <Routes>
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<HomePage initialHomeContent={hydrationPageData.homeContent} initialArticles={hydrationPageData.homeArticles} />} />
                  <Route path="/faq" element={<FaqPage initialPage={hydrationPageData.faq} />} />
                  <Route path="/about" element={<ContentPage slug="about" initialPage={hydrationPageData.about} />} />
                  <Route path="/contact" element={<ContentPage slug="contact" initialPage={hydrationPageData.contact} />} />
                  <Route path="/privacy" element={<ContentPage slug="privacy" initialPage={hydrationPageData.privacy} />} />
                  <Route path="/terms" element={<ContentPage slug="terms" initialPage={hydrationPageData.terms} />} />
                  <Route path="/learn" element={<LearnLibraryPage initialResult={hydrationPageData.learnResult} initialCategories={hydrationPageData.learnCategories} />} />
                  <Route path="/learn/:slug" element={<ArticlePage initialArticle={hydrationPageData.article} />} />
                  <Route path="/:slug" element={<ToolPage initialToolSeo={hydrationPageData.toolSeo} />} />
                </Route>

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="platforms" element={<AdminPlatforms />} />
                  <Route path="tools" element={<AdminTools />} />
                  <Route path="seo" element={<AdminSEOContent />} />
                  <Route path="content" element={<AdminContent />} />
                  <Route path="ads" element={<AdminAds />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="logs" element={<AdminLogs />} />
                  <Route path="articles" element={<AdminArticles />} />
                  <Route path="articles/new" element={<AdminArticleEditor />} />
                  <Route path="articles/:articleId" element={<AdminArticleEditor />} />
                  <Route path="article-categories" element={<AdminArticleCategories />} />
                  <Route path="home-content" element={<AdminHomeContent />} />
                </Route>

                <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </AdminAuthProvider>
          </RouterComponent>
          <Toaster position="top-center" richColors closeButton />
        </AppProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
